/**
 * Shopify Cart API - With Zero Default Count
 */

(function () {
  // Create namespace for cart functions
  window.ShopifyCart = {};

  /**
   * Add product to cart
   * @param {number|string} variantId - Variant ID of product
   * @param {number} quantity - Quantity to add
   * @param {Object} properties - Additional properties (optional)
   * @returns {Promise} - Promise containing added item data
   */
  window.ShopifyCart.addToCart = async function (
    variantId,
    quantity = 1,
    properties = {}
  ) {
    try {
      // Use JSON approach for modern Shopify stores
      const payload = {
        items: [
          {
            id: variantId,
            quantity: quantity,
            ...(Object.keys(properties).length > 0 && {
              properties: properties,
            }),
          },
        ],
      };

      // Get CSRF token if available
      const csrfToken = document
        .querySelector('meta[name="csrf-token"]')
        ?.getAttribute("content");

      const response = await fetch("/cart/add.js", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Accept: "application/json",
          "X-Requested-With": "XMLHttpRequest",
          ...(csrfToken && { "X-CSRF-Token": csrfToken }),
        },
        body: JSON.stringify(payload),
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(
          errorData.description || `HTTP error! status: ${response.status}`
        );
      }

      const result = await response.json();
      console.log("Product added to cart:", result);

      // Get updated cart
      const updatedCart = await window.ShopifyCart.refreshCart();

      // Trigger custom event similar to what we saw in SachaDrake
      window.ShopifyCart.triggerProductAddedEvent(result);

      // First try to refresh the cart drawer content
      await window.ShopifyCart.refreshCartDrawerContent(updatedCart);

      // Then try to open the cart drawer
      window.ShopifyCart.openCartDrawer();

      return result;
    } catch (error) {
      console.error("Error adding to cart:", error);
      throw error;
    }
  };

  /**
   * Refresh cart data
   * @returns {Promise} - Promise containing updated cart data
   */
  window.ShopifyCart.refreshCart = async function () {
    try {
      const response = await fetch("/cart.js", {
        method: "GET",
        headers: {
          "Content-Type": "application/json",
          Accept: "application/json",
          "X-Requested-With": "XMLHttpRequest",
        },
      });

      if (!response.ok) {
        throw new Error(`Failed to refresh cart: ${response.status}`);
      }

      const cart = await response.json();
      console.log("Updated cart data:", cart);

      // Update UI with new cart data
      window.ShopifyCart.updateCartUI(cart);

      return cart;
    } catch (error) {
      console.error("Error refreshing cart:", error);
      throw error;
    }
  };

  /**
   * Refresh cart drawer content
   */
  window.ShopifyCart.refreshCartDrawerContent = async function (cart) {
    try {
      // APPROACH 1: Try to fetch the cart drawer section
      try {
        const response = await fetch("/?section_id=mini-cart");
        if (response.ok) {
          const html = await response.text();
          // Find the cart drawer contents
          const tempDiv = document.createElement("div");
          tempDiv.innerHTML = html;

          // Look for the inner contents
          const drawerContents = tempDiv.querySelector(
            "#mini-cart .drawer__inner, #mini-cart .drawer__content"
          );
          if (drawerContents) {
            // Replace the current drawer contents
            const currentDrawerContents = document.querySelector(
              "#mini-cart .drawer__inner, #mini-cart .drawer__content"
            );
            if (currentDrawerContents) {
              currentDrawerContents.innerHTML = drawerContents.innerHTML;
              console.log(
                "Cart drawer contents updated with section rendering"
              );
              return true;
            }
          }
        }
      } catch (e) {
        console.log("Section rendering approach failed:", e);
      }

      // APPROACH 2: Try using AJAX to update the cart drawer
      if (
        window.Shopify &&
        window.Shopify.theme &&
        typeof window.Shopify.theme.sections === "object"
      ) {
        const miniCartSection = window.Shopify.theme.sections.registered.find(
          (section) =>
            section.type === "mini-cart" || section.id.includes("mini-cart")
        );

        if (
          miniCartSection &&
          typeof miniCartSection.onCartUpdate === "function"
        ) {
          miniCartSection.onCartUpdate(cart);
          console.log("Updated cart drawer using theme section method");
          return true;
        }
      }

      // APPROACH 3: Use fetch to get cart HTML directly
      try {
        const response = await fetch("/cart?view=drawer");
        if (response.ok) {
          const html = await response.text();
          const miniCart = document.querySelector("#mini-cart");
          if (miniCart) {
            miniCart.innerHTML = html;
            console.log("Updated cart drawer HTML from /cart?view=drawer");
            return true;
          }
        }
      } catch (e) {
        console.log("Cart drawer HTML fetch failed:", e);
      }

      return false;
    } catch (error) {
      console.error("Error refreshing cart drawer content:", error);
      return false;
    }
  };

  /**
   * Open cart drawer - specifically targeting the cart drawer
   */
  window.ShopifyCart.openCartDrawer = function () {
    // ===== METHOD 1: Look for a cart-specific button or link =====
    // Find cart-specific buttons by looking for cart-related attributes
    const cartLinks = Array.from(
      document.querySelectorAll('a[href="/cart"], a[aria-controls="mini-cart"]')
    );

    // Filter down to find the one most likely to be the cart toggle
    // Look for common cart button classes/attributes
    const cartButton = cartLinks.find((link) => {
      const classList = link.className.toLowerCase();
      const ariaLabel = (link.getAttribute("aria-label") || "").toLowerCase();

      return (
        classList.includes("cart") ||
        ariaLabel.includes("cart") ||
        link.querySelector(".icon-cart, .cart-icon, .icon-shopping-cart")
      );
    });

    if (cartButton) {
      console.log("Found cart-specific button, clicking it");
      cartButton.click();
      return;
    }

    // ===== METHOD 2: Look for specific cart drawer functions =====
    // Try finding the theme's cart drawer specific functions
    if (window.theme) {
      // Common theme function patterns specifically for cart
      const possibleFunctions = [
        "openCartDrawer",
        "openCart",
        "showCart",
        "toggleCart",
        "showCartDrawer",
      ];

      for (const funcName of possibleFunctions) {
        if (typeof window.theme[funcName] === "function") {
          console.log(`Found theme.${funcName} function, calling it`);
          window.theme[funcName]();
          return;
        }

        // Check in nested objects
        if (
          window.theme.cart &&
          typeof window.theme.cart[funcName] === "function"
        ) {
          console.log(`Found theme.cart.${funcName} function, calling it`);
          window.theme.cart[funcName]();
          return;
        }
      }
    }

    // ===== METHOD 3: Find cart-specific toggle elements =====
    // Look specifically for cart toggle elements
    const cartToggle = document.querySelector(
      '[data-action="open-drawer"][data-drawer-id="mini-cart"], ' +
        "[data-cart-toggle], " +
        ".js-cart-trigger, " +
        '.cart-link[aria-controls="mini-cart"], ' +
        ".header__icon--cart, " +
        ".js-mini-cart-trigger"
    );

    if (cartToggle) {
      console.log("Found cart toggle element, clicking it");
      cartToggle.click();
      return;
    }

    // ===== METHOD 4: Manually trigger a click on the cart icon =====
    // This is a more specific approach to find the right cart icon
    // Look for any link containing a cart icon
    const headerIcons = document.querySelectorAll(
      ".header__icon-wrapper, .header__actions a, .site-header__icons a"
    );
    for (const icon of headerIcons) {
      // Look for cart icon or shopping bag icon
      const hasCartIcon = icon.querySelector(
        ".icon-cart, .icon-bag, .cart-icon, .fa-shopping-cart, .fa-shopping-bag"
      );
      const ariaLabel = (icon.getAttribute("aria-label") || "").toLowerCase();

      if (
        hasCartIcon ||
        ariaLabel.includes("cart") ||
        ariaLabel.includes("bag")
      ) {
        // This is specifically targeting the shopping cart icon, not search
        console.log("Found cart icon in header, clicking it");
        icon.click();
        return;
      }
    }

    // ===== METHOD 5: Last resort, just try to show the mini-cart directly =====
    const miniCart = document.querySelector("#mini-cart");
    if (miniCart) {
      miniCart.classList.add(
        "active",
        "is-open",
        "drawer--is-open",
        "open",
        "is-visible"
      );
      document.body.classList.add(
        "mini-cart-open",
        "drawer-open",
        "js-drawer-open"
      );
      console.log("Forced cart drawer visibility directly");
    } else {
      console.log("Could not find the mini-cart element");
    }
  };

  /**
   * Update cart UI elements (count, total, etc.)
   * Now ensuring bubble count always shows, even when zero
   * @param {Object} cart - Cart data from Shopify
   */
  window.ShopifyCart.updateCartUI = function (cart) {
    console.log("Updating cart UI with count:", cart.item_count);

    // Target the specific bubble-count class first
    const bubbleCountElements = document.querySelectorAll(".bubble-count");
    if (bubbleCountElements.length > 0) {
      console.log(`Found ${bubbleCountElements.length} bubble-count elements`);
      bubbleCountElements.forEach((el) => {
        // Always show the count, even when zero
        el.textContent = cart.item_count;
        console.log(
          "Updated bubble-count element with value:",
          cart.item_count
        );

        // Make sure it's always visible, even when zero
        el.style.display = "";
        el.classList.remove("hide", "hidden", "is-hidden");
      });
    } else {
      // If no bubble-count elements found, try to create them
      console.log("No bubble-count elements found, trying to create them");

      // Look for cart icons where we might need to add a bubble count
      const cartIcons = document.querySelectorAll(
        'a[href="/cart"] .icon-cart, a[href="/cart"] .cart-icon'
      );
      cartIcons.forEach((icon) => {
        // Check if the icon already has a bubble count
        let bubbleCount = icon.parentElement.querySelector(".bubble-count");
        if (!bubbleCount) {
          // Create a new bubble count element
          bubbleCount = document.createElement("span");
          bubbleCount.className = "bubble-count";
          bubbleCount.style.cssText =
            "position: absolute; top: -8px; right: -8px; background: red; color: white; border-radius: 50%; min-width: 18px; height: 18px; text-align: center; font-size: 12px; line-height: 18px; font-weight: bold;";

          // Make sure the parent has a relative position for absolutely positioned bubble
          if (getComputedStyle(icon.parentElement).position === "static") {
            icon.parentElement.style.position = "relative";
          }

          icon.parentElement.appendChild(bubbleCount);
          console.log("Created new bubble-count element");
        }

        // Update the bubble count
        bubbleCount.textContent = cart.item_count;
      });
    }

    // Fallback to other common selectors for cart count
    const cartCountSelectors = [
      ".cart-count",
      "[data-cart-count]",
      ".js-cart-count",
      ".cart-count-bubble",
      ".header__cart-count",
      ".cart-bubble-count",
      ".site-header__cart-count",
      ".cart-link__bubble-num",
      ".cart-link__count",
      ".cart__count",
      ".cart-count-number",
      ".js-cart-item-count",
      ".header-cart__count",
      ".icon-cart-badge",
      ".icon__badge",
      ".badge--cart-items",
      "[data-cart-item-count]",
      "[data-cart-items-count]",
      "[data-header-cart-count]",
      "[data-cart-count-bubble]",
    ];

    // Join all selectors for one comprehensive query
    const cartCountElements = document.querySelectorAll(
      cartCountSelectors.join(", ")
    );

    if (cartCountElements.length > 0) {
      // Update all found elements
      cartCountElements.forEach((el) => {
        // Update text content and make sure it's visible
        el.textContent = cart.item_count;
        el.style.display = "";
        el.classList.remove("hide", "hidden", "is-hidden");
      });
    }

    // Update cart total
    const cartTotalSelectors = [
      ".cart-total",
      "[data-cart-total]",
      ".js-cart-total",
      ".cart-subtotal__price",
      ".cart__total",
      ".mini-cart__total-price",
    ];

    const cartTotalElements = document.querySelectorAll(
      cartTotalSelectors.join(", ")
    );

    if (cartTotalElements.length > 0) {
      const formattedTotal = window.ShopifyCart.formatMoney(cart.total_price);
      cartTotalElements.forEach((el) => {
        el.textContent = formattedTotal;
      });
    }
  };

  /**
   * Format money amount according to shop's currency format
   * @param {number} cents - Amount in cents
   * @returns {string} - Formatted price
   */
  window.ShopifyCart.formatMoney = function (cents) {
    if (typeof cents === "string") cents = cents.replace(".", "");
    const value = parseInt(cents || 0, 10);

    // Try to use shop's money format if available
    if (typeof window.theme?.moneyFormat === "string") {
      return window.theme.moneyFormat.replace(
        "{{amount}}",
        (value / 100).toFixed(2)
      );
    }

    // Default formatter
    const formatter = new Intl.NumberFormat("en-US", {
      style: "currency",
      currency: "PHP", // Change to your store's currency
      minimumFractionDigits: 2,
    });

    return formatter.format(value / 100);
  };

  /**
   * Trigger custom event when product is added to cart
   * @param {Object} item - Added item data
   */
  window.ShopifyCart.triggerProductAddedEvent = function (item) {
    // Generate random ID similar to what we saw in logs
    const randomId =
      Math.random().toString(36).substring(2, 15) +
      Math.random().toString(36).substring(2, 15);

    // Create event data
    const eventData = {
      id: `sh-${randomId}`,
      name: "product_added_to_cart",
      data: { item: item },
      timestamp: new Date().toISOString(),
      type: "standard",
    };

    // Create and dispatch custom event
    const event = new CustomEvent("product_added_to_cart", {
      detail: eventData,
      bubbles: true,
    });

    document.dispatchEvent(event);
    console.log("Triggered product_added_to_cart event:", eventData);
  };

  // Initialize - fetch cart data when script loads
  window.ShopifyCart.refreshCart()
    .then((cart) => console.log("Cart initialized:", cart))
    .catch((error) => console.error("Error initializing cart:", error));

  // Log message confirming initialization
  console.log(
    "ShopifyCart functions exposed to window object. Use window.ShopifyCart.addToCart() to add products."
  );
})();

const subscribeToTolstoyAddToCartClick = () => {
  const myCallback = (payload) => {
    console.log("tolstoy payload", payload);
    const drawerElement = document.querySelector(".mini-cart .drawer__content");
    drawerElement.classList.remove("drawer__content--center");
    window.ShopifyCart.addToCart(payload.variantId, payload.quantity)
      .then((result) => console.log("Added successfully:", result))
      .catch((error) => console.error("Error:", error));
  };

  const options = {};

  window.tolstoyWidget.subscribe("tolstoy_add_to_cart", myCallback, options);
};

if (window.tolstoyWidget) {
  subscribeToTolstoyAddToCartClick();
} else {
  window.addEventListener("tolstoyWidgetReady", () => {
    subscribeToTolstoyAddToCartClick();
    // Additional logic...
  });
}
